import React, { useEffect, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import { getDefaultLanguage, translate } from '../../../services/translate';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import '../../../styles/full-calendar.css';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { dispatchService } from '../../../services/central-api/dispatch';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import moment from 'moment';
import { View } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import CustomButton from '../../../components/CustomButton';
import { titleCasePt } from '../../../services/title-case-pt';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { WindowInformation } from '../../../services/window-information';
import TouchableOpacityWithMiddleClick from '../../../components/TouchableOpacityWithMiddleClick';
import { useNavigation, NavigationProp } from '@react-navigation/native';
import { DispatchParamList } from '../../../typings/Params';
import { openInNewTab } from '../../../services/navigation-assistant';
import { dispatchModule } from '../../../routes/dispatch';
import { useCurrentApp } from '../../../context/CurrentApp';

interface TeamsFromTeamNameProps {
    teamNameId: number;
}

export default function TeamsFromTeamName({ teamNameId }: TeamsFromTeamNameProps) {
    const [language, setLanguage] = useState('pt-br');
    const calendarRef = useRef<FullCalendar | null>(null);
    const { user: authenticatedUser } = useAuth();
    const { styles } = useStyles(stylesheet);
    const [startDate, setStartDate] = useState<number>(0);
    const [endDate, setEndDate] = useState<number>(0);
    const [title, setTitle] = useState('');
    const windowInfo = WindowInformation();
    const [currentView, setCurrentView] = useState<'dayGridMonth' | 'timeGridWeek' | 'timeGridDay'>('timeGridWeek');
    const navigation = useNavigation<NavigationProp<DispatchParamList>>();
    const currentApp = useCurrentApp();

    useEffect(() => {
        getLanguage();
    }, []);

    async function getLanguage() {
        const language = await getDefaultLanguage();
        setLanguage(language);
    }

    const { data } = useQuery({
        queryKey: ['teamsFromTeamName', teamNameId, startDate, endDate],
        queryFn: () => dispatchService.getTeamsFromTeamName(teamNameId!, { startDate, endDate }),
        enabled: Boolean(teamNameId) && Boolean(authenticatedUser) && startDate !== 0 && endDate !== 0,
    });

    function updateTitle() {
        const api = calendarRef.current?.getApi();
        if (!api) {
            return;
        }
        setTitle(api.view.title);
    }

    const handleDatesSet = (info: { startStr: string; endStr: string; }) => {
        setStartDate(moment(info.startStr).valueOf());
        setEndDate(moment(info.endStr).valueOf());
        updateTitle();
    };

    function ViewSelector() {
        return <View style={styles.headerGroup}>
            <CustomButton
                type='secondary'
                text={translate('month')}
                isActive={currentView === 'dayGridMonth'}
                onPress={() => {
                    setCurrentView('dayGridMonth');
                    calendarRef.current?.getApi().changeView('dayGridMonth');
                }}
            />
            <CustomButton
                type='secondary'
                text={translate('week')}
                isActive={currentView === 'timeGridWeek'}
                onPress={() => {
                    setCurrentView('timeGridWeek');
                    calendarRef.current?.getApi().changeView('timeGridWeek');
                }}
            />
            <CustomButton
                type='secondary'
                text={translate('day')}
                isActive={currentView === 'timeGridDay'}
                onPress={() => {
                    setCurrentView('timeGridDay');
                    calendarRef.current?.getApi().changeView('timeGridDay');
                }}
            />
        </View>;
    }

    function Title() {
        return <View style={styles.headerGroup}>
            <MyAppText style={{
                fontSize: 20,
                fontWeight: 'bold',
                textAlign: 'center',
            }}>{titleCasePt(title)}</MyAppText>
        </View>;
    }

    function Directions(props: { showToday: boolean; }) {
        return <View style={styles.headerGroup}>
            <CustomButton
                type='secondary'
                icon={faChevronLeft}
                onPress={() => {
                    calendarRef.current?.getApi().prev();

                }}
            />
            <CustomButton
                type='secondary'
                icon={faChevronRight}
                onPress={() => {
                    calendarRef.current?.getApi().next();
                }}
            />
            {props.showToday &&
                <CustomButton
                    type='secondary'
                    text={translate('today')}
                    onPress={() => {
                        calendarRef.current?.getApi().today();
                    }}
                />
            }
        </View>;
    }

    return <View style={{ rowGap: 15, flex: 1, }}>
        {windowInfo.isMobile ?
            <View style={{ alignSelf: 'center', gap: 4 }}>
                <ViewSelector />
                <Directions showToday={false} />
                <Title />
            </View>
            : <View style={{ justifyContent: 'space-between', gap: 4, flexDirection: 'row' }}>
                <Directions showToday={true} />
                <Title />
                <ViewSelector />
            </View>
        }
        <FullCalendar
            height='100%'
            locales={language == 'pt-br' ? [ptBrLocale] : []}
            locale={language == 'pt-br' ? 'pt-br' : undefined}
            allDaySlot={false}
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin]}
            initialView='timeGridWeek'
            headerToolbar={false}
            eventContent={(arg) => {
                if (arg.view.type === 'dayGridMonth') {
                    return <TouchableOpacityWithMiddleClick
                        style={{
                            backgroundColor: arg.backgroundColor,
                            width: '100%',
                            minHeight: 22,
                            borderRadius: 3,
                            overflow: 'hidden',
                        }}
                        onPress={() => {
                            if (dispatchModule.screens.UnitForm.canAccess(authenticatedUser, currentApp)) {
                                navigation.navigate('UnitForm', { id: Number(arg.event.title) });
                            }
                        }}
                        onMiddleClick={() => {
                            if (dispatchModule.screens.UnitForm.canAccess(authenticatedUser, currentApp)) {
                                openInNewTab('Dispatch', { screen: 'UnitForm', params: { id: Number(arg.event.title) } });
                            }
                        }}
                    >
                        <MyAppText style={{
                            color: arg.textColor,
                            fontSize: 10,
                        }}>{moment(arg.event.start!).format('HH:mm')} - {moment(arg.event.end!).format('HH:mm')}</MyAppText>
                        <MyAppText style={styles.monthEventText(arg.textColor)}>{translate('team')}: #{arg.event.title}</MyAppText>
                    </TouchableOpacityWithMiddleClick>;
                }
                return <TouchableOpacityWithMiddleClick
                    style={{
                        overflow: 'hidden',
                        height: '100%',
                    }}
                    onPress={() => {
                        if (dispatchModule.screens.UnitForm.canAccess(authenticatedUser, currentApp)) {
                            navigation.navigate('UnitForm', { id: Number(arg.event.title) });
                        }
                    }}
                    onMiddleClick={() => {
                        if (dispatchModule.screens.UnitForm.canAccess(authenticatedUser, currentApp)) {
                            openInNewTab('Dispatch', { screen: 'UnitForm', params: { id: Number(arg.event.title) } });
                        }
                    }}
                >
                    <MyAppText style={{
                        color: arg.textColor,
                        fontSize: 10,
                    }}>{arg.timeText}</MyAppText>
                    <MyAppText style={styles.weekAndDayEventText(arg.textColor)}>{translate('team')}: #{arg.event.title}</MyAppText>
                </TouchableOpacityWithMiddleClick>;
            }}
            dayHeaderContent={(arg) => {

                if (arg.view.type === 'dayGridMonth') {
                    return <div>{arg.text}</div>;
                } else {
                    const isToday = arg.isToday;

                    return <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: 8 }}>{arg.date.toLocaleDateString('pt-BR', { weekday: 'short' })}</span>
                        <View style={[{
                            width: 28,
                            height: 28,
                            alignContent: 'center',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }, isToday ? {
                            backgroundColor: '#2563eb',
                            borderRadius: 9999,
                        } : {}]}>
                            <MyAppText style={[isToday ? {
                                color: '#ffffff',
                            } : {}]}>{arg.date.getDate()}</MyAppText>
                        </View>
                    </div>;
                }

            }}
            datesSet={handleDatesSet}
            weekends={true}
            events={(data ?? []).map(h => ({
                title: String(h.id),
                start: h.start,
                end: h.finish,
                color: UnistylesRuntime.themeName == 'dark' ? '#204935' : '#d0eadc',
                textColor: UnistylesRuntime.themeName == 'dark' ? '#20b06d' : '#0b5433'
            }))}

        />
    </View>;
}

const stylesheet = createStyleSheet(() => ({
    monthEventText: (textColor: string) => ({
        color: textColor,
        whiteSpace: 'nowrap',
    }),
    weekAndDayEventText: (textColor: string) => ({
        color: textColor,
        whiteSpace: 'normal',
        wordBreak: 'keep-all',
        overflowWrap: 'normal',
    }),
    headerGroup: {
        flexDirection: 'row',
        alignSelf: 'center',
        gap: 2,
    }
}));
